import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import { Crown, Check, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 8;

export default function CheckoutSuccessPage() {
  const { t } = useT();
  const { setPremium } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment: null, error: null });
  const attemptsRef = useRef(0);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState({ status: "error", error: "missing_session_id" });
      return;
    }
    try {
      const res = await api.get(`/payments/checkout/status/${sessionId}`);
      const ps = res.data?.payment_status;
      const s = res.data?.status;
      if (ps === "paid") {
        setPremium(true);
        setState((prev) => {
          if (prev.status !== "paid") {
            toast.success(t("checkout.toast_paid") || "Premium activated 🚀");
          }
          return { status: "paid", payment: res.data };
        });
        return;
      }
      if (s === "expired") {
        setState({ status: "expired", payment: res.data });
        return;
      }
      // still pending — keep polling
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        setState({ status: "timeout", payment: res.data });
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    } catch (err) {
      setState({ status: "error", error: err?.response?.data?.detail?.message || err.message });
    }
  }, [sessionId, setPremium]);

  useEffect(() => {
    poll();
  }, [poll]);

  return (
    <div className="min-h-screen bg-radial-neon text-white flex flex-col">
      <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 py-10 flex-1 flex flex-col">
        <Logo to="/" />
        <div
          className="mt-12 mx-auto w-full max-w-md rounded-3xl border border-white/10 bg-zinc-950/70 backdrop-blur-sm p-8 text-center"
          data-testid="checkout-success-card"
        >
          {state.status === "polling" && (
            <>
              <Loader2 className="mx-auto text-neon animate-spin" size={36} />
              <h1 className="mt-5 font-display text-2xl font-black">
                {t("checkout.verifying") || "Zahlung wird bestätigt..."}
              </h1>
              <p className="mt-2 text-sm text-zinc-400">
                {t("checkout.verifying_sub") ||
                  "Wir prüfen deine Zahlung bei Stripe. Das dauert nur einen Moment."}
              </p>
            </>
          )}

          {state.status === "paid" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-neon to-blue-500 grid place-items-center text-black shadow-neon">
                <Crown size={28} />
              </div>
              <h1
                className="mt-5 font-display text-3xl font-black"
                data-testid="checkout-success-title"
              >
                {t("checkout.success_h") || "Willkommen, Premium-Pilot!"}
              </h1>
              <p className="mt-2 text-sm text-zinc-300">
                {t("checkout.success_sub") ||
                  "Deine Zahlung wurde bestätigt. Alle Premium-Features sind jetzt freigeschaltet."}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1.5 rounded-full">
                <Check size={12} />
                {(state.payment?.amount_total
                  ? `${(state.payment.amount_total / 100).toFixed(2)} ${(state.payment.currency || "eur").toUpperCase()}`
                  : "Premium")} ·{" "}
                {t("checkout.success_active") || "aktiv"}
              </div>
              <div className="mt-7 flex flex-col gap-2">
                <Button
                  asChild
                  className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold shadow-neon"
                  data-testid="checkout-success-dashboard"
                >
                  <Link to="/app">{t("checkout.go_dashboard") || "Zum Dashboard"}</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full bg-transparent border-white/10 text-white hover:bg-white/5"
                >
                  <Link to="/app/profile">
                    {t("checkout.see_profile") || "Profil ansehen"}
                  </Link>
                </Button>
              </div>
            </>
          )}

          {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
            <>
              <AlertCircle className="mx-auto text-amber-400" size={36} />
              <h1 className="mt-5 font-display text-2xl font-black">
                {t("checkout.error_h") || "Zahlung konnte nicht bestätigt werden"}
              </h1>
              <p className="mt-2 text-sm text-zinc-400">
                {state.status === "expired"
                  ? t("checkout.expired_sub") || "Diese Checkout-Session ist abgelaufen."
                  : state.status === "timeout"
                  ? t("checkout.timeout_sub") ||
                    "Wir konnten deine Zahlung nicht bestätigen. Falls dein Konto belastet wurde, schreib uns eine Mail an support@ficopilot.app."
                  : t("checkout.error_sub") ||
                    "Etwas ist schiefgelaufen. Versuch es erneut oder kontaktiere den Support."}
              </p>
              <div className="mt-6 flex flex-col gap-2">
                <Button
                  asChild
                  className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold"
                  data-testid="checkout-error-retry"
                >
                  <Link to="/pricing">{t("checkout.retry") || "Erneut versuchen"}</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full bg-transparent border-white/10 text-white hover:bg-white/5"
                >
                  <Link to="/app">{t("checkout.go_dashboard") || "Zum Dashboard"}</Link>
                </Button>
              </div>
            </>
          )}
        </div>
        <Footer variant="compact" />
      </div>
    </div>
  );
}
